// src/features/organisation/components/positions/PositionManagement.tsx
import React, { useState } from 'react';
import { Briefcase, CheckCircle, AlertTriangle, Plus, Trash2, Users } from 'lucide-react';
import DataTable from '../../../../components/ui/DataTable';
import { usePositions, usePositionActions } from '../../hooks/usePositions';
import { Position } from '../../../../types/organisation';
import OrganisationNavigation from '../OrganisationNavigation';
import PositionDetailForm from './PositionDetailForm';

const PositionManagement: React.FC = () => {
  const { positions, loading, refresh } = usePositions();
  const { deletePosition } = usePositionActions();
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Position[]>([]);

  const columns = [
    { key: 'positionCode', title: 'Code', sortable: true },
    { key: 'positionName', title: 'Nom du poste', sortable: true },
    {
      key: 'department',
      title: 'Département',
      render: (value: unknown) => value?.departmentName || 'Non défini',
    },
    {
      key: 'isManagementPosition',
      title: 'Management',
      render: (value: boolean) => value ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Users className="h-3 w-3 mr-1" />
          Management
        </span>
      ) : null,
    },
    {
      key: 'employeeCount',
      title: 'Employés',
      render: (value: number) => (
        <span className="text-sm text-gray-600">{value || 0} employé{(value || 0) > 1 ? 's' : ''}</span>
      ),
    },
    {
      key: 'isActive',
      title: 'Statut',
      render: (value: boolean) => value ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Actif
        </span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Inactif
        </span>
      ),
    },
  ];

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return;
    if (!window.confirm(`Supprimer ${selectedRows.length} poste(s) ?`)) return;
    try {
      await Promise.all(selectedRows.map(p => deletePosition(p.id)));
      setSelectedRows([]);
      await refresh();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleClose = async () => {
    setSelectedId(undefined);
    await refresh();
  };

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 rounded-md p-2 md:p-3">
              <Briefcase className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">{positions?.length || 0}</div>
              <div className="text-xs md:text-sm text-gray-600">Postes total</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-2 md:p-3">
              <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {positions?.filter(p => p.isActive).length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Postes actifs</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-500 rounded-md p-2 md:p-3">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {positions?.filter(p => p.isManagementPosition).length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Postes management</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-orange-500 rounded-md p-2 md:p-3">
              <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {positions?.filter(p => !p.isActive).length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Postes inactifs</div>
            </div>
          </div>
        </div>
      </div>

      {/* En-tête + Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Briefcase className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Postes</h1>
            <p className="text-gray-600">Gérez les postes de travail et leurs attributions</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRows.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              <span>Supprimer ({selectedRows.length})</span>
            </button>
          )}
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Nouveau poste</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <DataTable
          data={positions || []}
          columns={columns}
          loading={loading}
          selectable={true}
          onSelectionChange={setSelectedRows}
          onRowDoubleClick={(pos) => setSelectedId(pos.id)}
          emptyMessage="Aucun poste trouvé"
        />
      </div>

      {/* Formulaire de détail */}
      {selectedId !== undefined && (
        <PositionDetailForm
          positionId={selectedId}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default PositionManagement;
